import { create } from 'zustand';
import services from '@C/CashiersOnDuty/_utils/data';
import utilServices from '@C/Utils/data';
import storeApis from '@stores/_utils/data';
import dayjs from 'dayjs';

const normalizeShiftOption = (shift) => ({
  value: shift?.uuid ?? shift?.value ?? '',
  label: shift?.shift ?? shift?.label ?? shift?.name ?? '',
  raw: shift,
});

const extractDutyShiftId = (dutyDetails = {}) =>
  dutyDetails?.shift_uuid ??
  dutyDetails?.shift_id ??
  dutyDetails?.uuid ??
  dutyDetails?.shift?.uuid ??
  '';

const normalizeBunkConfig = (configuration) => {
  if (!configuration) {
    return {};
  }

  const source =
    configuration.configuration ?? configuration.data ?? configuration;

  if (Array.isArray(source)) {
    const nestedRecord = source.find((item) => item?.configuration);
    if (nestedRecord?.configuration) {
      return normalizeBunkConfig(nestedRecord.configuration);
    }

    return source.reduce((acc, item) => {
      if (item?.id) {
        acc[item.id] =
          item.value ?? item.checked ?? item.enabled ?? item.text ?? '';
      }
      return acc;
    }, {});
  }

  if (typeof source === 'object') {
    return Object.entries(source).reduce((acc, [key, value]) => {
      if (key !== 'uuid' && key !== 'id') {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  return {};
};

const extractBunkConfigRecord = (data) => {
  if (Array.isArray(data)) {
    return data[0] ?? null;
  }

  if (data?.configuration) {
    return data;
  }

  return null;
};

export const useGlobalStore = create((set, get) => ({
  creditCustomers: [],
  creditCustomersOptions: [],
  productList: [],
  productListOptions: [],
  taxList: [],
  staffList: [],
  staffListOptions: [],
  codGrandTotal: 0,
  codTotalCollected: 0,
  codStaff: '',
  codShift: '',
  codShiftId: '',
  shortage: 0,
  codCollectionData: null,
  navigationData: [],
  bannerData: [],
  isLoading: false,
  isFetchingNavigation: false,
  claimedOffers: [],
  shiftList: [],
  bunkConfig: {},
  bunkConfigUuid: null,
  fetchNavigationData: async () => {
    const { isFetchingNavigation } = get();
    if (isFetchingNavigation) return; // If isFetching is true, return early
    set({ isFetchingNavigation: true }); // Set isFetching to true before API call
    try {
      const response = await storeApis.getNavigationData();
      const sortedData = response.data.data.sort((a, b) => a.weight - b.weight);
      set({ navigationData: sortedData, isFetchingNavigation: false }); // Set isFetching to false after API call
    } catch (error) {
      console.log(error);
      set({ isFetchingNavigation: false }); // Set isFetching to false after API call
    }
  },
  fetchBanners: async () => {
    try {
      const response = await storeApis.getBanners();
      const sortedData = response.data.data.sort(
        (a, b) => a.priority - b.priority,
      );
      const filteredData = sortedData.filter(
        (banner) =>
          banner.is_active &&
          banner.expiry_date >= dayjs().format('YYYY-MM-DDTHH:mm:ss'),
      );
      let claimedOffers = get().getClaimedOffers();
      // filter out claimed offers from banner data
      const unclaimedData = filteredData.filter((banner) => {
        return !claimedOffers.some(
          (claimed) => claimed.content === banner.content,
        );
      });
      const finalData = unclaimedData.map((banner) => {
        const parts = banner.content.split('--------');
        return {
          ...banner,
          content: parts[0].trim(),
          buttons: parts[1]
            ? (() => {
                try {
                  // Clean up the string by removing escape characters
                  const cleanedString = parts[1]
                    .trim()
                    .replace(/\\r\\n/g, '') // Remove \r\n
                    .replace(/\r\n/g, '') // Remove actual line breaks
                    .replace(/\n/g, '') // Remove newlines
                    .replace(/\r/g, ''); // Remove carriage returns

                  const parsed = JSON.parse(cleanedString);

                  // If it's a single object, wrap it in an array
                  // If it's already an array, return as is
                  return Array.isArray(parsed) ? parsed : [parsed];
                } catch (error) {
                  console.warn('Failed to parse buttons data:', error);
                  console.warn('Content that failed to parse:', parts[1]);
                  return [];
                }
              })()
            : [],
        };
      });
      set({ bannerData: finalData });
    } catch (error) {
      console.log(error);
      return [];
    }
  },
  getClaimedOffers: () => {
    const claimedOffers = localStorage.getItem('claimedOffers');
    return claimedOffers ? JSON.parse(claimedOffers) : [];
  },
  setClaimedOffers: (claimedOffers) => {
    // update the claimedOffers state and also update localStorage but dont add duplicates
    let existingOffers = get().getClaimedOffers();
    claimedOffers.forEach((offer) => {
      if (
        !existingOffers.some((existing) => existing.content === offer.content)
      ) {
        existingOffers.push(offer);
      }
    });
    set({ claimedOffers: existingOffers });
    // update bannerData to remove claimed offers
    const updatedBanners = get().bannerData.filter(
      (banner) =>
        !existingOffers.some((claimed) => claimed.content === banner.content),
    );
    set({ bannerData: updatedBanners });
    localStorage.setItem('claimedOffers', JSON.stringify(existingOffers));
  },
  fetchBunkConfig: async () => {
    try {
      const response = await storeApis.getBunkConfig();
      const data = response?.data?.data ?? response?.data ?? [];
      const record = extractBunkConfigRecord(data);
      const configSource =
        record?.configuration ??
        data?.configuration ??
        data?.data ??
        data ??
        [];

      set({
        bunkConfig: normalizeBunkConfig(configSource),
        bunkConfigUuid: record?.uuid ?? null,
      });
    } catch (error) {
      console.log(error);
    }
  },
  setBunkConfig: (configuration, uuid) =>
    set({
      bunkConfig: normalizeBunkConfig(configuration),
      bunkConfigUuid: uuid ?? get().bunkConfigUuid,
    }),
  setCreditCustomers: (creditCustomers) =>
    set({
      creditCustomers: creditCustomers,
      creditCustomersOptions: creditCustomers.map((item) => ({
        value: item.uuid,
        label: `${item.basic_details.first_name} ${item.basic_details.last_name}`,
        gst_no: item.gstn,
        notes: item.notes || item.credit_customer?.notes || '',
        active: item.is_active,
      })),
    }),
  fetchCreditCustomer: (customerId) => {
    let customers = get().creditCustomers;
    return customers.find((customer) => customer.uuid === customerId);
  },
  setProductList: (productList) =>
    set({
      productList: productList,
      productListOptions: productList.map((item) => ({
        value: item.uuid,
        label: item.product || item.name,
        price: item.price,
        product_type: item.hasOwnProperty('number_of_units') ? 'Lubes' : 'Fuel',
        stock: item.hasOwnProperty('number_of_units')
          ? item.number_of_units
          : 'Available',
      })),
    }),
  setTaxList: (taxList) =>
    set({
      taxList: taxList.map((item) => {
        const taxPercentage = item.tax_list.reduce(
          (a, b) => a + (b['percentage'] || 0),
          0,
        );
        return {
          value: [...(item.tax_list || [])],
          label: item.name,
          multiplier: taxPercentage,
        };
      }),
    }),
  setCODGrandTotal: async (duty, staff) => {
    try {
      const response = await services.getGrandTotal(staff, duty);
      const dutyDetails =
        response?.data?.data?.cashier_duty?.cashier?.duty_details || {};

      set({
        codStaff: `${response.data.data.cashier_duty.cashier.first_name} ${response.data.data.cashier_duty.cashier.last_name}`,
        codShift: dutyDetails.shift_name || dutyDetails.shift || '',
        codShiftId: extractDutyShiftId(dutyDetails),
      });
    } catch (error) {
      console.log(error);
    }
  },
  syncCODCollectionData: async (duty, staff) => {
    try {
      const response = await services.getTotalCollection(staff, duty);
      set({
        codCollectionData: response.data.data,
        codGrandTotal: response.data.data.expected_amount,
        codTotalCollected: response.data.data.actual_amount,
        shortage: response.data.data.shortage,
      });
    } catch (error) {
      console.log(error);
    }
  },
  refreshStaffList: async () => {
    try {
      const staffResponse = await utilServices.getStaffList();
      const data = staffResponse.data.data;

      const cashiers = (data.cashiers || []).map((item) => ({
        value: item.uuid,
        label: `${item.first_name} ${item.last_name}`,
      }));

      const otherStaff = (data.other_staff || []).map((item) => ({
        value: item.uuid,
        label: `${item.first_name} ${item.last_name}`,
      }));

      const dutiesResponse =
        await utilServices.getFilteredStaffList('2077-01-01');
      const duties = dutiesResponse.data.data.duties_list || [];

      const dealers = [];
      const managers = [];

      duties.forEach((item) => {
        const entry = {
          value: item.staff_uuid,
          label: `${item.first_name} ${item.last_name}`,
        };

        if (item.role_type === 101) {
          dealers.push(entry);
        } else {
          managers.push(entry);
        }
      });

      const options = [
        { label: 'Cashiers', options: cashiers },
        { label: 'Dealer', options: dealers },
        { label: 'Managers', options: managers },
        { label: 'Other Staff', options: otherStaff },
      ];

      set({ staffList: options });
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  },
  fetchShiftList: async () => {
    try {
      const response = await utilServices.getShiftsList();
      const shiftList = (response?.data?.data || [])
        .map(normalizeShiftOption)
        .filter((shift) => shift.value && shift.label);

      set({ shiftList });
      return shiftList;
    } catch (error) {
      console.log(error);
      return [];
    }
  },
  setShiftList: (shiftList) =>
    set({
      shiftList: (shiftList || [])
        .map(normalizeShiftOption)
        .filter((shift) => shift.value && shift.label),
    }),
  setStaffList: (staffList) => set({ staffList: staffList }),
}));
